import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Flex,
  Link,
  Text,
} from "@chakra-ui/react";
import { formatInTimeZone } from "date-fns-tz";
import { RiRestartLine } from "react-icons/ri";
import { Link as RouterLink } from "react-router-dom";
import { EmptyState } from "src/components/EmptyState";
import { useTimezoneName } from "src/hooks/useOrganization";
import Vacation from "src/images/vacation.svg?react";
import * as Url from "src/services/url";
import { GetFormAlertsByFormIdResponse } from "src/types/formTemplate/v2";
import * as RD from "src/types/remoteData";

export interface Props {
  alerts: GetFormAlertsByFormIdResponse;
  onRetry: (alert: GetFormAlertsByFormIdResponse["data"][number]) => void;
  isRetrying: boolean;
  organization: RD.RemoteData<unknown, { path: string }>;
  enrollmentPeriodId: string;
  formTemplateId: string;
  formId: string;
}

const makeAlertNameLink = (
  alertName: string,
  organization: RD.RemoteData<unknown, { path: string }>,
  enrollmentPeriodId: string,
  formTemplateId: string
) => {
  return (
    <Link
      as={RouterLink}
      to={organization
        .map((org) => {
          const baseUrl = Url.OrgAdmin.FormTemplates.edit(
            org,
            enrollmentPeriodId,
            formTemplateId
          );
          return `${baseUrl}?enrollmentPeriod=${enrollmentPeriodId}#rules`;
        })
        .withDefault("#")}
      fontWeight={"bold"}
      textDecoration="underline"
      color="red.900"
      _hover={{ textDecoration: "none" }}
    >
      {alertName}
    </Link>
  );
};

const makeAddressQuestionLink = (
  organization: RD.RemoteData<unknown, { path: string }>,
  formTemplateId: string,
  formId: string
) => {
  return (
    <Link
      as={RouterLink}
      to={Url.OrgAdmin.Forms.view({
        organization,
        formTemplateId,
        id: formId,
      })}
      color="red.900"
      reloadDocument
      fontWeight={"bold"}
      textDecoration="underline"
      _hover={{ textDecoration: "none" }}
    >
      address question
    </Link>
  );
};

const makeLearnMoreLink = (href: string) => {
  return (
    <Link
      href={href}
      isExternal
      fontWeight={"bold"}
      color="red.900"
      textDecoration="underline"
      _hover={{ textDecoration: "none" }}
    >
      Learn more
    </Link>
  );
};

const makeAlertDescriptionNode = (
  alert: GetFormAlertsByFormIdResponse["data"][number],
  timezoneName: string,
  organization: RD.RemoteData<unknown, { path: string }>,
  enrollmentPeriodId: string,
  formTemplateId: string,
  formId: string
) => {
  if (alert.category === "rule" && alert.subcategory === "geocoding")
    return makeGeoAlertDescriptionNode(
      alert,
      timezoneName,
      organization,
      enrollmentPeriodId,
      formTemplateId,
      formId
    );
  else if (
    alert.category === "rule" &&
    alert.subcategory === "invalid-school-grade"
  )
    return makeInvalidSchoolGradeAlertDescriptionNode(
      alert,
      timezoneName,
      organization,
      enrollmentPeriodId,
      formTemplateId,
      formId
    );
  return (
    <Text>
      <Text as="span" color="red.900">
        An unknown error has occurred.
      </Text>
    </Text>
  );
};

const makeGeoAlertDescriptionNode = (
  alert: GetFormAlertsByFormIdResponse["data"][number],
  timezoneName: string,
  organization: RD.RemoteData<unknown, { path: string }>,
  enrollmentPeriodId: string,
  formTemplateId: string,
  formId: string
) => {
  return (
    <Text>
      <Text as="span" color="red.900">
        Error:{" "}
        {makeAlertNameLink(
          alert.alert_name,
          organization,
          enrollmentPeriodId,
          formTemplateId
        )}{" "}
        failed due to an invalid or incomplete{" "}
      </Text>
      {makeAddressQuestionLink(organization, formTemplateId, formId)}
      {". "}
      {makeLearnMoreLink(
        "https://help.avela.org/hc/en-us/articles/46425167200141-Fixing-invalid-address-error"
      )}
      <Text color="red.900" as="span">
        {". "}
      </Text>
      <Text as="span" color="gray" fontWeight={"300"}>
        {formatInTimeZone(
          alert.created_at,
          timezoneName,
          "MM/dd/yyyy, hh:mm a zzz"
        )}
      </Text>
    </Text>
  );
};

const makeInvalidSchoolGradeAlertDescriptionNode = (
  alert: GetFormAlertsByFormIdResponse["data"][number],
  timezoneName: string,
  organization: RD.RemoteData<unknown, { path: string }>,
  enrollmentPeriodId: string,
  formTemplateId: string,
  formId: string
) => {
  return (
    <Text>
      <Text as="span" color="red.900">
        Error:{" "}
        {makeAlertNameLink(
          alert.alert_name,
          organization,
          enrollmentPeriodId,
          formTemplateId
        )}{" "}
        failed due to an invalid school and grade combination. Update school
        selection to resolve this error.{" "}
      </Text>
      {". "}
      {makeLearnMoreLink(
        "https://help.avela.org/hc/en-us/articles/46425192158861-Fixing-school-grade-mismatch-error"
      )}
      <Text color="red.900" as="span">
        {". "}
      </Text>
      <Text as="span" color="gray" fontWeight={"300"}>
        {formatInTimeZone(
          alert.created_at,
          timezoneName,
          "MM/dd/yyyy, hh:mm a zzz"
        )}
      </Text>
    </Text>
  );
};

const RuleAlert: React.FC<{
  alertDescriptionNode: React.ReactNode;
  canRetry: boolean;
  onRetry: () => void;
  isRetrying: boolean;
}> = ({ alertDescriptionNode, onRetry, isRetrying, canRetry }) => {
  return (
    <Alert status="error" variant="subtle" borderRadius={6}>
      <AlertIcon alignSelf="flex-center" />
      <AlertDescription width="100%" flex="1">
        {alertDescriptionNode}
      </AlertDescription>
      {canRetry && (
        <Button
          leftIcon={<RiRestartLine />}
          colorScheme="black"
          _hover={{ borderColor: "gray.700" }}
          borderRadius={"6px"}
          borderColor={"gray.400"}
          variant="outline"
          onClick={canRetry ? onRetry : () => {}}
          isLoading={isRetrying}
        >
          Retry
        </Button>
      )}
    </Alert>
  );
};

export const FormAlerts: React.FC<Props> = ({
  alerts,
  onRetry,
  isRetrying,
  organization,
  enrollmentPeriodId,
  formTemplateId,
  formId,
}) => {
  const timezoneName = useTimezoneName();
  return (
    <>
      {!alerts.data.length && (
        <Flex
          width="100%"
          minHeight="60vh"
          alignItems="center"
          justifyContent="center"
        >
          <EmptyState
            description="Nothing to worry about!"
            heading="No alerts"
            Svg={Vacation}
          ></EmptyState>
        </Flex>
      )}
      {alerts.data.length > 0 && (
        <Flex direction={"column"} marginTop={"1rem"} gap={"1rem"}>
          {alerts.data.map((alert) => (
            <RuleAlert
              alertDescriptionNode={makeAlertDescriptionNode(
                alert,
                timezoneName,
                organization,
                enrollmentPeriodId,
                formTemplateId,
                formId
              )}
              canRetry={alert.can_retry}
              onRetry={() => onRetry(alert)}
              isRetrying={isRetrying}
              key={alert.id}
            />
          ))}
        </Flex>
      )}
    </>
  );
};
